import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const TaskDetailsPanel = ({ task, onTaskUpdate, onClose }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedTask, setEditedTask] = useState(task);

  if (!task) {
    return (
      <div className="h-full flex flex-col items-center justify-center bg-card border-l border-border px-6">
        <Icon name="FileText" size={64} color="var(--color-muted-foreground)" />
        <p className="mt-4 text-muted-foreground text-center">
          Select a task to view details
        </p>
      </div>
    );
  }

  const statusOptions = [
    { value: 'pending', label: 'Pending' },
    { value: 'in-progress', label: 'In Progress' },
    { value: 'completed', label: 'Completed' },
    { value: 'delayed', label: 'Delayed' }
  ];

  const priorityOptions = [
    { value: 'low', label: 'Low' },
    { value: 'medium', label: 'Medium' },
    { value: 'high', label: 'High' },
    { value: 'critical', label: 'Critical' }
  ];

  const handleSave = () => {
    onTaskUpdate(editedTask);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditedTask(task);
    setIsEditing(false);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'var(--color-success)';
      case 'in-progress':
        return 'var(--color-primary)';
      case 'delayed':
        return 'var(--color-error)';
      case 'pending':
        return 'var(--color-warning)';
      default:
        return 'var(--color-muted-foreground)';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical':
        return 'var(--color-error)';
      case 'high':
        return 'var(--color-warning)';
      case 'medium':
        return 'var(--color-primary)';
      case 'low':
        return 'var(--color-success)';
      default:
        return 'var(--color-muted-foreground)';
    }
  };

  return (
    <div className="h-full flex flex-col bg-card border-l border-border">
      <div className="flex items-center justify-between px-4 py-4 border-b border-border">
        <h2 className="text-lg font-semibold text-foreground">Task Details</h2>
        <div className="flex items-center gap-2">
          {!isEditing ? (
            <>
              <Button
                variant="outline"
                size="sm"
                iconName="Edit"
                onClick={() => setIsEditing(true)}
              >
                Edit
              </Button>
              <Button
                variant="ghost"
                size="sm"
                iconName="X"
                onClick={onClose}
              />
            </>
          ) : (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={handleCancel}
              >
                Cancel
              </Button>
              <Button
                variant="default"
                size="sm"
                iconName="Check"
                onClick={handleSave}
              >
                Save
              </Button>
            </>
          )}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto px-4 py-4">
        <div className="space-y-6">
          <div>
            {isEditing ? (
              <Input
                label="Task Name"
                value={editedTask?.name}
                onChange={(e) => setEditedTask({ ...editedTask, name: e?.target?.value })}
              />
            ) : (
              <>
                <label className="text-sm font-medium text-muted-foreground">Task Name</label>
                <h3 className="text-xl font-semibold text-foreground mt-1">{task?.name}</h3>
              </>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              {isEditing ? (
                <Select
                  label="Status"
                  options={statusOptions}
                  value={editedTask?.status}
                  onChange={(value) => setEditedTask({ ...editedTask, status: value })}
                />
              ) : (
                <>
                  <label className="text-sm font-medium text-muted-foreground">Status</label>
                  <div className="flex items-center gap-2 mt-1">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: getStatusColor(task?.status) }}
                    />
                    <span className="text-sm font-medium text-foreground capitalize">
                      {task?.status?.replace('-', ' ')}
                    </span>
                  </div>
                </>
              )}
            </div>

            <div>
              {isEditing ? (
                <Select
                  label="Priority"
                  options={priorityOptions}
                  value={editedTask?.priority}
                  onChange={(value) => setEditedTask({ ...editedTask, priority: value })}
                />
              ) : (
                <>
                  <label className="text-sm font-medium text-muted-foreground">Priority</label>
                  <div className="flex items-center gap-2 mt-1">
                    <Icon
                      name="AlertCircle"
                      size={16}
                      color={getPriorityColor(task?.priority)}
                    />
                    <span className="text-sm font-medium text-foreground capitalize">
                      {task?.priority}
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              {isEditing ? (
                <Input
                  label="Duration (hours)"
                  type="number"
                  value={editedTask?.duration}
                  onChange={(e) => setEditedTask({ ...editedTask, duration: parseInt(e?.target?.value) })}
                />
              ) : (
                <>
                  <label className="text-sm font-medium text-muted-foreground">Duration</label>
                  <p className="text-sm text-foreground mt-1">{task?.duration} hours</p>
                </>
              )}
            </div>

            <div>
              {isEditing ? (
                <Input
                  label="Workers"
                  type="number"
                  value={editedTask?.workers}
                  onChange={(e) => setEditedTask({ ...editedTask, workers: parseInt(e?.target?.value) })}
                />
              ) : (
                <>
                  <label className="text-sm font-medium text-muted-foreground">Workers</label>
                  <p className="text-sm text-foreground mt-1">{task?.workers} workers</p>
                </>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              {isEditing ? (
                <Input
                  label="Start Date"
                  type="date"
                  value={editedTask?.startDate}
                  onChange={(e) => setEditedTask({ ...editedTask, startDate: e?.target?.value })}
                />
              ) : (
                <>
                  <label className="text-sm font-medium text-muted-foreground">Start Date</label>
                  <p className="text-sm text-foreground mt-1">
                    {new Date(task.startDate)?.toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric'
                    })}
                  </p>
                </>
              )}
            </div>

            <div>
              {isEditing ? (
                <Input
                  label="End Date"
                  type="date"
                  value={editedTask?.endDate}
                  onChange={(e) => setEditedTask({ ...editedTask, endDate: e?.target?.value })}
                />
              ) : (
                <>
                  <label className="text-sm font-medium text-muted-foreground">End Date</label>
                  <p className="text-sm text-foreground mt-1">
                    {new Date(task.endDate)?.toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric'
                    })}
                  </p>
                </>
              )}
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-muted-foreground">Progress</label>
            <div className="mt-2">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-foreground">{task?.progress}%</span>
                <span className="caption text-muted-foreground">
                  {task?.actualHours || 0} / {task?.duration}h
                </span>
              </div>
              <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-250"
                  style={{
                    width: `${task?.progress}%`,
                    backgroundColor: getStatusColor(task?.status)
                  }}
                />
              </div>
            </div>
          </div>

          {task?.dependencies && task?.dependencies?.length > 0 && (
            <div>
              <label className="text-sm font-medium text-muted-foreground">Dependencies</label>
              <div className="mt-2 space-y-2">
                {task?.dependencies?.map((depId, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-2 px-3 py-2 bg-muted rounded-lg"
                  >
                    <Icon name="GitBranch" size={16} color="var(--color-primary)" />
                    <span className="text-sm text-foreground">Task #{depId}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {task?.assignedTo && task?.assignedTo?.length > 0 && (
            <div>
              <label className="text-sm font-medium text-muted-foreground">Assigned To</label>
              <div className="mt-2 space-y-2">
                {task?.assignedTo?.map((person, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-3 px-3 py-2 bg-muted rounded-lg"
                  >
                    <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-semibold">
                      {person?.name?.split(' ')?.map(n => n?.[0])?.join('')}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-foreground">{person?.name}</p>
                      <p className="caption text-muted-foreground">{person?.role}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {task?.notes && (
            <div>
              <label className="text-sm font-medium text-muted-foreground">Notes</label>
              <p className="text-sm text-foreground mt-2 whitespace-pre-wrap">{task?.notes}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TaskDetailsPanel;